import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { STATUS, USER_IDENTITY_PREFERENCE } from '~/literals';

import { ChangePasswordParams, ErrorResponse, ForgotPasswordParams, LoginParams, UserProfileParams, UserState } from '~/types';

export const userState: UserState = {
  isAuthenticated: false,
  status: STATUS.IDLE,
  errorMessage: '',
  user: null,
  requestSuccess: false,
  userIdentityPreference: USER_IDENTITY_PREFERENCE.NOT_SELECTED,
  returnUrl: '',
  userDeleteErrorMessage: '',
  userDeleteSuccess: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState: userState,
  reducers: {
    userPageLoaded: draft => {
      draft.errorMessage = '';
      draft.requestSuccess = false;
    },
    login: (draft, _: PayloadAction<LoginParams>) => {
      draft.status = STATUS.RUNNING;
    },
    checkUserSessionValidity: () => {
    },
    setUserUnAuthenticated: (draft) => {
      draft.isAuthenticated = false;
    },
    loginSuccess: (draft, { payload }: PayloadAction<UserState>) => {
      draft.isAuthenticated = true;
      draft.status = STATUS.SUCCESS;
      draft.user = payload.user;
      draft.errorMessage = '';
    },
    loginFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.isAuthenticated = false;
      draft.status = STATUS.ERROR;
      draft.errorMessage = payload.errorMessage;
    },
    logOut: draft => {
      draft.status = STATUS.RUNNING;
    },
    logOutSuccess: (draft) => {
      draft.isAuthenticated = false;
      draft.status = STATUS.IDLE;
      draft.errorMessage = '';
      draft.user = null;
    },
    register: (draft, _: PayloadAction<UserProfileParams>) => {
      draft.status = STATUS.RUNNING;
    },
    registerSuccess: draft => {
      draft.isAuthenticated = true;
      draft.status = STATUS.READY;
      draft.errorMessage = '';
      draft.userDeleteSuccess = false;
    },
    registerFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.isAuthenticated = false;
      draft.status = STATUS.ERROR;
      draft.errorMessage = payload.errorMessage;
    },
    forgotPassword: (draft, _: PayloadAction<ForgotPasswordParams>) => {
      draft.status = STATUS.RUNNING;
      draft.requestSuccess = false;
    },
    forgotPasswordSuccess: draft => {
      draft.isAuthenticated = false;
      draft.status = STATUS.READY;
      draft.errorMessage = '';
      draft.requestSuccess = true;
    },
    forgotPasswordFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.isAuthenticated = false;
      draft.status = STATUS.ERROR;
      draft.errorMessage = payload.errorMessage;
      draft.requestSuccess = false;
    },
    changePassword: (draft, _: PayloadAction<ChangePasswordParams>) => {
      draft.status = STATUS.RUNNING;
      draft.requestSuccess = false;
      draft.isAuthenticated = false;
    },
    changePasswordSuccess: draft => {
      draft.status = STATUS.READY;
      draft.errorMessage = '';
      draft.requestSuccess = true;
    },
    changePasswordFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.isAuthenticated = false;
      draft.status = STATUS.ERROR;
      draft.errorMessage = payload.errorMessage;
      draft.requestSuccess = false;
    },
    timeDelay: (_, __: PayloadAction<number>) => {
    },
    setUserIdentityPreference: (draft, { payload }: PayloadAction<string>) => {
      draft.userIdentityPreference = payload;
    },
    setUserReturnUrl: (draft, { payload }: PayloadAction<string>) => {
      draft.returnUrl = payload;
    },
    updateUserProfile: (draft, _: PayloadAction<UserProfileParams>) => {
      draft.status = STATUS.RUNNING;
    },
    updateUserProfileSuccess: draft => {
      draft.status = STATUS.SUCCESS;
      draft.errorMessage = '';
    },
    updateUserProfileFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.status = STATUS.ERROR;
      draft.errorMessage = payload.errorMessage;
    },
    updateUserProfileActionCompleted: (draft, { payload }: PayloadAction<UserState>) => {
      draft.user = payload.user;
    },
    deleteAccount: (draft) => {
      draft.status = STATUS.RUNNING;
    },
    deleteAccountSuccess: draft => {
      draft.status = STATUS.SUCCESS;
      draft.userDeleteErrorMessage = '';
      draft.isAuthenticated = false;
      draft.userDeleteSuccess = true;
    },
    deleteAccountFailure: (draft, { payload }: PayloadAction<ErrorResponse>) => {
      draft.isAuthenticated = false;
      draft.status = STATUS.ERROR;
      draft.userDeleteErrorMessage = payload.errorMessage;
      draft.userDeleteSuccess = false;
    },
    resetUserActions: (draft) => {
      draft.status = STATUS.IDLE;
      draft.errorMessage = '';
      draft.userDeleteErrorMessage = '';
      draft.userDeleteSuccess = false;
    }
  },
});

export const {
  forgotPassword,
  forgotPasswordFailure,
  forgotPasswordSuccess,
  login,
  loginFailure,
  loginSuccess,
  logOut,
  logOutSuccess,
  register,
  registerFailure,
  registerSuccess,
  userPageLoaded,
  changePassword,
  changePasswordFailure,
  changePasswordSuccess,
  timeDelay,
  setUserIdentityPreference,
  setUserReturnUrl,
  checkUserSessionValidity,
  setUserUnAuthenticated,
  updateUserProfile,
  updateUserProfileSuccess,
  updateUserProfileFailure,
  deleteAccount,
  deleteAccountSuccess,
  deleteAccountFailure,
  resetUserActions,
  updateUserProfileActionCompleted,
} = userSlice.actions;
export default userSlice.reducer;
